/**
 * AppConfig Validator
 *
 * Zod schema for validating AppConfig at load time.
 */

import { z } from 'zod';
import { shadcnPageConfigSchema } from '../shadcn/schema';

const crudFeatureFieldSchema = z.object({
  name: z.string(),
  type: z.enum([
    'string',
    'number',
    'boolean',
    'date',
    'datetime',
    'email',
    'url',
    'enum',
    'text',
    'reference',
    'file',
    'image',
  ]),
  label: z.string().optional(),
  required: z.boolean().optional(),
  enumValues: z.array(z.string()).optional(),
  referencedEntity: z.string().optional(),
  renderer: z.string().optional(),
  columnRenderer: z.string().optional(),
  showInList: z.boolean().optional(),
  showInDetail: z.boolean().optional(),
  showInForm: z.boolean().optional(),
  sortable: z.boolean().optional(),
  filterable: z.boolean().optional(),
  searchable: z.boolean().optional(),
  validation: z.object({
    min: z.number().optional(),
    max: z.number().optional(),
    minLength: z.number().optional(),
    maxLength: z.number().optional(),
    pattern: z.string().optional(),
  }).optional(),
});

const entityActionSchema = z.object({
  name: z.string(),
  label: z.string(),
  icon: z.string().optional(),
  variant: z.enum(['primary', 'secondary', 'danger']).optional(),
  apiMethod: z.enum(['POST', 'PUT', 'PATCH']).optional(),
  apiPath: z.string().optional(),
});

interface SidebarNavItem {
  label: string;
  href: string;
  icon?: string;
  badge?: string;
  children?: SidebarNavItem[];
  requiredPermission?: string;
}

const sidebarNavSchema: z.ZodType<SidebarNavItem> = z.lazy(() =>
  z.object({
    label: z.string(),
    href: z.string(),
    icon: z.string().optional(),
    badge: z.string().optional(),
    children: z.array(sidebarNavSchema).optional(),
    requiredPermission: z.string().optional(),
  }),
) as z.ZodType<SidebarNavItem>;

const entityConfigSchema = z.object({
  name: z.string(),
  pluralName: z.string(),
  slug: z.string(),
  apiPath: z.string(),
  icon: z.string().optional(),
  fields: z.array(crudFeatureFieldSchema),
  pages: z.array(z.enum(['list', 'detail', 'create', 'edit'])),
  bulkActions: z.boolean().optional(),
  exportable: z.boolean().optional(),
  importable: z.boolean().optional(),
  softDelete: z.boolean().optional(),
  customActions: z.array(entityActionSchema).optional(),
  permissions: z.object({
    list: z.string().optional(),
    create: z.string().optional(),
    read: z.string().optional(),
    update: z.string().optional(),
    delete: z.string().optional(),
  }).optional(),
});

const pageConfigSchema = z.object({
  id: z.string(),
  title: z.string(),
  path: z.string(),
  icon: z.string().optional(),
  intentComponent: z.string().optional(),
  intentProps: z.record(z.unknown()).optional(),
  widgets: z.array(z.object({
    id: z.string(),
    title: z.string(),
    type: z.enum(['plugin', 'kpi-card', 'chart', 'chart-bar', 'chart-line', 'chart-pie', 'entity-summary', 'activity-feed', 'recent-items']),
    size: z.enum(['sm', 'md', 'lg']).optional(),
    description: z.string().optional(),
    dataSource: z.union([z.record(z.unknown()), z.string()]),
  })).optional(),
  requiredPermission: z.string().optional(),
  theme: z.object({
    palette: z.object({
      background: z.string().optional(),
      text: z.string().optional(),
    }).optional(),
    colorScheme: z.object({
      supportsDarkMode: z.boolean().optional(),
      light: z.object({
        background: z.string().optional(),
        text: z.string().optional(),
      }).optional(),
      dark: z.object({
        background: z.string().optional(),
        text: z.string().optional(),
      }).optional(),
    }).optional(),
  }).optional(),
});

const brandingConfigSchema = z.object({
  logoUrl: z.string().optional(),
  logoText: z.string().optional(),
  faviconUrl: z.string().optional(),
});

const authConfigSchema = z.object({
  provider: z.enum(['none', 'custom', 'clerk', 'auth0']),
  loginUrl: z.string().optional(),
  logoutUrl: z.string().optional(),
  loginEndpoint: z.string().optional(),
  registerEndpoint: z.string().optional(),
  tokenHeader: z.string().optional(),
  tokenStorageKey: z.string().optional(),
  roles: z.array(z.string()).optional(),
  permissions: z.record(z.array(z.string())).optional(),
  userIdClaim: z.string().optional(),
});

const realtimeConfigSchema = z.object({
  wsUrl: z.string(),
  reconnectStrategy: z.object({
    enabled: z.boolean().optional(),
    initialDelayMs: z.number().optional(),
    maxDelayMs: z.number().optional(),
    factor: z.number().optional(),
    maxAttempts: z.number().optional(),
  }).optional(),
  presenceChannel: z.string().optional(),
});

const storageConfigSchema = z.object({
  uploadUrl: z.string(),
});

const i18nConfigSchema = z.object({
  defaultLocale: z.string(),
  supportedLocales: z.array(z.string()).min(1).optional(),
  locales: z.array(z.string()).min(1).optional(),
  translationsEndpoint: z.string().optional(),
}).refine(
  value => (
    Array.isArray(value.supportedLocales)
    || Array.isArray(value.locales)
  ),
  {
    message: 'i18n.supportedLocales or i18n.locales must be provided',
  },
);

const billingConfigSchema = z.object({
  provider: z.enum(['stripe', 'none']),
  publishableKey: z.string().optional(),
  pricingTableId: z.string().optional(),
  customerPortalUrl: z.string().optional(),
  entitlements: z.record(z.array(z.string())).optional(),
  pluginLayoutEntitlements: z.record(z.array(z.string())).optional(),
});

const pluginModuleRefSchema = z.object({
  module: z.string(),
  exportName: z.string().optional(),
});


const HEX_COLOR_REGEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
const HSL_COLOR_REGEX = /^hsla?\(\s*(?:\d|[1-2]\d{1,2}|3[0-5]\d|360)(?:deg|rad|turn)?\s*,\s*(?:\d|[1-9]\d|100)%\s*,\s*(?:\d|[1-9]\d|100)%\s*(?:,\s*(?:0|1|0?\.\d+))?\s*\)$/i;
// Shadcn CSS-variable format: bare "H S% L%" e.g. "221 83% 53%"
const HSL_BARE_REGEX = /^\d+(\.\d+)?\s+\d+(\.\d+)?%\s+\d+(\.\d+)?%$/;

function isHexOrHslColor(value: string): boolean {
  const normalized = value.trim();
  return HEX_COLOR_REGEX.test(normalized) || HSL_COLOR_REGEX.test(normalized) || HSL_BARE_REGEX.test(normalized);
}

const shadcnThemeSchema = z.object({
  primary: z.string().refine(isHexOrHslColor, 'theme.primary must be a valid hex or hsl/hsla color').optional(),
  secondary: z.string().refine(isHexOrHslColor, 'theme.secondary must be a valid hex or hsl/hsla color').optional(),
  radius: z.string().optional(),
  background: z.string().refine(isHexOrHslColor, 'theme.background must be a valid hex or hsl/hsla color').optional(),
  foreground: z.string().refine(isHexOrHslColor, 'theme.foreground must be a valid hex or hsl/hsla color').optional(),
  muted: z.string().refine(isHexOrHslColor, 'theme.muted must be a valid hex or hsl/hsla color').optional(),
  accent: z.string().refine(isHexOrHslColor, 'theme.accent must be a valid hex or hsl/hsla color').optional(),
}).optional();
const pluginConfigSchema = z.object({
  name: z.string(),
  fieldRenderers: z.record(pluginModuleRefSchema).optional(),
  columnRenderers: z.record(pluginModuleRefSchema).optional(),
  actionButtons: z.record(pluginModuleRefSchema).optional(),
  sidebarWidgets: z.record(pluginModuleRefSchema).optional(),
});


const runtimeRoleSchema = z.enum(['viewer', 'editor', 'admin', 'owner']);

const appConfigBaseSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string(),
  version: z.string().optional(),
  presetId: z.string().min(1),
  fallbackPresetId: z.string().optional(),
  apiBaseUrl: z.string(),
  features: z.array(z.enum([
    'auth', 'analytics', 'export', 'bulk-actions',
    'notifications', 'search', 'keyboard-shortcuts', 'audit-log', 'settings',
  ])),
  branding: brandingConfigSchema.optional(),
  sidebar: z.array(sidebarNavSchema),
  entities: z.array(entityConfigSchema),
  pages: z.array(pageConfigSchema),
  auth: authConfigSchema.optional(),
  realtime: realtimeConfigSchema.optional(),
  storage: storageConfigSchema.optional(),
  i18n: i18nConfigSchema.optional(),
  plugins: z.array(pluginConfigSchema).optional(),
  billing: billingConfigSchema.optional(),
  shadcnMigrationStatus: z.enum(['none', 'preview', 'migrated']).optional(),
  theme: shadcnThemeSchema,
  shadcn: z.union([z.boolean(), z.literal(true)]).optional(),
  roles: z.record(runtimeRoleSchema).optional(),
  plan: z.enum(['free', 'pro', 'enterprise']).optional(),
});

export const appConfigSchema = appConfigBaseSchema;

export const shadcnAppConfigSchema = z.object({
  shadcn: z.literal(true),
  appId: z.string().min(1),
  appName: z.string().optional(),
  description: z.string().optional(),
  apiBaseUrl: z.string().optional(),
  plan: z.enum(['free', 'pro', 'enterprise']).optional(),
  theme: shadcnThemeSchema,
  auth: z.object({
    loginEndpoint: z.string().optional(),
    registerEndpoint: z.string().optional(),
  }).optional(),
  sidebar: z.array(z.object({
    label: z.string(),
    href: z.string(),
    icon: z.string().optional(),
  })).optional(),
  pages: z.array(shadcnPageConfigSchema).min(1),
  shadcnMigrationStatus: z.enum(['none', 'preview', 'migrated']).optional(),
}).passthrough();

export type ValidatedAppConfig = z.infer<typeof appConfigSchema>;
export type ValidatedShadcnAppConfig = z.infer<typeof shadcnAppConfigSchema>;
